import React from 'react';
import { useLocalization } from '../hooks/useLocalization';
import type { TranslationKey } from '../services/localization';

interface LocalizedTextProps {
  /** Translation key for text */
  textKey?: TranslationKey;
  /** Raw text to display (if not using translation key) */
  text?: string;
  /** Additional CSS classes */
  className?: string;
  /** HTML tag to render */
  as?: keyof JSX.IntrinsicElements;
}

interface LocalizedDateProps {
  /** Date to format */
  date: Date | string;
  /** Format type */
  format?: 'short' | 'medium' | 'long';
  /** Show time as well */
  showTime?: boolean;
  /** Show relative time (e.g., "2 дня назад") */
  relative?: boolean;
  /** Additional CSS classes */
  className?: string;
}

interface LocalizedNumberProps {
  /** Number to format */
  value: number;
  /** Number format options */
  options?: Intl.NumberFormatOptions;
  /** Additional CSS classes */
  className?: string;
}

interface LocalizedWeightProps {
  /** Weight in kg */
  weight: number;
  /** Additional CSS classes */
  className?: string;
}

interface LocalizedDurationProps {
  /** Duration in minutes */
  minutes: number;
  /** Additional CSS classes */
  className?: string;
}

interface LocalizedVolumeProps {
  /** Volume in kg */
  volume: number;
  /** Additional CSS classes */
  className?: string;
}

/**
 * Component for displaying localized text
 */
export const LocalizedText: React.FC<LocalizedTextProps> = ({
  textKey,
  text,
  className = '',
  as: Component = 'span'
}) => {
  const { t } = useLocalization();
  
  const displayText = textKey ? t(textKey) : text || '';
  
  return React.createElement(Component, { className }, displayText);
};

/**
 * Component for displaying localized dates
 */
export const LocalizedDate: React.FC<LocalizedDateProps> = ({
  date,
  format = 'medium',
  showTime = false,
  relative = false,
  className = ''
}) => {
  const { formatDate, formatDateTime, formatRelativeTime } = useLocalization();
  
  let formattedDate: string;
  
  if (relative) {
    formattedDate = formatRelativeTime(date);
  } else if (showTime) {
    formattedDate = formatDateTime(date, format);
  } else {
    formattedDate = formatDate(date, format);
  }
  
  return <span className={className}>{formattedDate}</span>;
};

/**
 * Component for displaying localized numbers
 */
export const LocalizedNumber: React.FC<LocalizedNumberProps> = ({
  value,
  options,
  className = ''
}) => {
  const { formatNumber } = useLocalization();
  
  return <span className={className}>{formatNumber(value, options)}</span>;
};

/**
 * Component for displaying localized weight
 */
export const LocalizedWeight: React.FC<LocalizedWeightProps> = ({
  weight,
  className = ''
}) => {
  const { formatWeight } = useLocalization();
  
  return <span className={className}>{formatWeight(weight)}</span>;
};

/**
 * Component for displaying localized duration
 */
export const LocalizedDuration: React.FC<LocalizedDurationProps> = ({
  minutes,
  className = ''
}) => {
  const { formatDuration } = useLocalization();
  
  return <span className={className}>{formatDuration(minutes)}</span>;
};

/**
 * Component for displaying localized volume
 */
export const LocalizedVolume: React.FC<LocalizedVolumeProps> = ({
  volume,
  className = ''
}) => {
  const { formatVolume } = useLocalization();
  
  return <span className={className}>{formatVolume(volume)}</span>;
};

/**
 * Component for displaying workout count with proper pluralization
 */
export const WorkoutCount: React.FC<{ count: number; className?: string }> = ({
  count,
  className = ''
}) => {
  const { formatWorkoutCount } = useLocalization();
  
  return <span className={className}>{formatWorkoutCount(count)}</span>;
};

/**
 * Component for displaying exercise count with proper pluralization
 */
export const ExerciseCount: React.FC<{ count: number; className?: string }> = ({
  count,
  className = ''
}) => {
  const { formatExerciseCount } = useLocalization();
  
  return <span className={className}>{formatExerciseCount(count)}</span>;
};

/**
 * Component for displaying set count with proper pluralization
 */
export const SetCount: React.FC<{ count: number; className?: string }> = ({
  count,
  className = ''
}) => {
  const { formatSetCount } = useLocalization();
  
  return <span className={className}>{formatSetCount(count)}</span>;
};